
import { useEffect, useRef, useState } from 'react';

export default function About() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Guard against environments where IntersectionObserver is not available (e.g., SSR)
    if (typeof IntersectionObserver === 'undefined') {
      // Immediately show the content if we cannot observe
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
          // Once visible we can stop observing to improve performance
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    // Clean‑up observer on unmount
    return () => {
      observer.disconnect();
    };
  }, []);

  const brands = ['Nike', 'Apple', 'Samsung', 'Sony', 'Adidas'];

  const skills = [
    'Product Reviews',
    'Unboxing',
    'Tutorials',
    'Lifestyle Content',
    'Brand Storytelling',
    'Social Media Content',
    'Video Editing',
    'Photography',
  ];

  return (
    <section
      id="about"
      ref={sectionRef}
      className="py-16 sm:py-24 md:py-32 bg-[#f5f5f5] relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-xs sm:text-sm font-mono text-gray-500 mb-8 sm:mb-16">
          /about-james
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 sm:gap-12 lg:gap-16">
          {/* Left Content */}
          <div className="lg:col-span-2">
            <div
              className={`transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light text-[#1a1a1a] leading-relaxed mb-8 sm:mb-12">
                I&apos;m{' '}
                <span className="font-bold text-[#00D9FF]">James Moore</span> — I create{' '}
                authentic content that resonates with real people. My approach combines creative
                storytelling with data‑driven strategy to deliver{' '}
                <span className="font-bold">measurable results</span> for brands.
              </h2>

              <p className="text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed mb-8">
                With over 3 years of experience in UGC creation, I&apos;ve helped dozens of brands
                connect with their audience through genuine, relatable content. Every project is an
                opportunity to tell a unique story that drives engagement and conversions.
              </p>

              <div className="mt-10 sm:mt-16">
                <p className="text-xs uppercase tracking-widest text-gray-500 mb-4 sm:mb-6 font-light">
                  Trusted by leading brands
                </p>
                <div className="flex flex-wrap items-center gap-4 sm:gap-6">
                  {brands.map((brand, index) => (
                    <div
                      key={brand}
                      className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white shadow-md flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-500 cursor-pointer hover:scale-110 ${
                        isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
                      }`}
                      style={{
                        transitionDelay: `${index * 100}ms`,
                        transform: `translateY(${index % 2 === 0 ? '0' : '10px'})`,
                      }}
                    >
                      <span className="text-xs sm:text-sm font-bold text-gray-800">
                        {brand}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Visual */}
          <div className="lg:col-span-1 flex flex-col items-center justify-center gap-6 sm:gap-8">
            <div
              className={`transition-all duration-1000 delay-300 ${
                isVisible ? 'opacity-100 scale-100 rotate-0' : 'opacity-0 scale-95 -rotate-3'
              }`}
            >
              <div className="relative group cursor-pointer">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20portrait%20of%20confident%20male%20content%20creator%20in%20casual%20modern%20outfit%20against%20minimalist%20white%20background%20high%20contrast%20black%20and%20white%20photography%20studio%20lighting%20sharp%20focus&width=500&height=500&seq=about-portrait-1&orientation=squarish"
                  alt="James Moore Portrait"
                  className="w-full max-w-[280px] sm:max-w-md rounded-[24px] sm:rounded-[32px] shadow-2xl transform -rotate-3 group-hover:rotate-0 group-hover:scale-105 transition-all duration-500"
                />
              </div>
            </div>

            {/* Skills Tag Cloud */}
            <div
              className={`flex flex-wrap gap-2 sm:gap-3 justify-center max-w-md transition-all duration-1000 delay-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              {skills.map((skill, index) => (
                <span
                  key={skill}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 bg-white text-gray-700 rounded-full text-xs sm:text-sm font-medium shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer whitespace-nowrap"
                  style={{ transitionDelay: `${index * 50}ms` }}
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
